import { enqueueSnackbar, closeSnackbar } from "notistack";
import requests from "./Requests";

/**
 * Perform a backend request with standard snackbar progress/success/error toasts.
 *
 * @param {string|null} group   Command prefix ("versioning", "users", ...) or null for raw commands.
 * @param {string} description  Human-readable label used in toasts ("Load version list").
 * @param {Array<string|number>} parts  Command tokens, joined with spaces.
 * @param {object} [options]
 * @param {object|File} [options.payload={}]  JSON body, or a File sent as binary.
 * @param {boolean} [options.silent=false]    No toasts at all (still throws on failure).
 * @param {boolean} [options.arrayIsError=false]  Treat an Array response as an expected
 *                  "error-shaped" success: shows an error toast but resolves with the array
 *                  so the caller can render it. Never set for commands whose normal
 *                  response is an array.
 * @param {string} [options.successMessage]   Override the "<description> successful!" toast.
 * @returns {Promise<any>} Parsed JSON response. Always rejects on transport/auth/backend
 *                         text errors (after showing the error toast, unless silent).
 */
let lastSilentWarning = 0;

export function perform(group, description, parts, options = {}) {
    // silent calls are the page loaders (idempotent reads): let the transport also retry transient failures.
    const { payload = {}, silent = false, arrayIsError = false, successMessage, retry = silent, timeout } = options;
    const command = (group ? group + " " : "") + parts.join(" ");
    const key = silent ? null : enqueueSnackbar(`${description}...`, { variant: "info", persist: true });

    return requests.request(command, payload, { retry, timeout })
        .then(response => {
            if (key !== null) closeSnackbar(key);
            if (arrayIsError && Array.isArray(response)) {
                if (!silent) enqueueSnackbar(`${description} returned errors.`, { variant: "error" });
                return response;
            }
            if (!silent) enqueueSnackbar(successMessage ?? `${description} successful!`, { variant: "success" });
            return response;
        })
        .catch(error => {
            if (key !== null) closeSnackbar(key);
            if (!silent) enqueueSnackbar(`${description} failed: ${error.message}`, { variant: "error" });
            else if (error.kind !== "auth" && Date.now() - lastSilentWarning > 45000) {
                // Background loads used to fail invisibly; surface one throttled warning so the user knows.
                lastSilentWarning = Date.now();
                enqueueSnackbar(`${description} failed (${error.message}); will retry on the next refresh.`, { variant: "warning" });
            }
            throw error;
        });
}

const api = {
    versioning:   (description, parts, options) => perform("versioning", description, parts, options),
    users:        (description, parts, options) => perform("users", description, parts, options),
    development:  (description, parts, options) => perform("development", description, parts, options),
    general:      (description, parts, options) => perform("general", description, parts, options),
    settings:     (description, parts, options) => perform("settings", description, parts, options),
    purchases:    (description, parts, options) => perform("iap", description, parts, options),
    health:       (description, parts, options) => perform("health", description, parts, options),
    raw:          (description, command, options) => perform(null, description, [command], options),
    // Any backend request group (declared by a Service): `api.group("versioning")("Loading", ["list"])`. A new service
    // needs no edit here; a mistyped named helper above still fails loudly as a TypeError.
    group:        (name) => (description, parts, options) => perform(name, description, parts, options),
};

export default api;
